/**
 * Модель опытов «Лаборатории» (CLAUDE.md §8): вся физика здесь, экраны только рисуют.
 * Тесты — frontend/tests/physics.test.ts (`npm test`): опыт с неверной физикой хуже,
 * чем никакой. Опыты одни и те же на сайте и в Telegram — бот открывает эту же
 * страницу кнопкой мини-приложения.
 */

export const G = 10 // Н/кг — как в задачах ОГЭ

/** Число по-русски: запятая вместо точки. */
export function ru(x: number, digits = 1): string {
  return x.toFixed(digits).replace('.', ',')
}

// ---------------------------------------------------------------- упругость

/**
 * Удлинение резиновой нити, мм. При малых деформациях — закон Гука, Δl = F/k;
 * при большой нагрузке нить тянется сильнее прямой (в опыте 100 г дали 60 мм
 * вместо 40): Δl = F/k · (1 + nonlin·F²), F в ньютонах.
 */
export function bandElongationMm(forceN: number, k: number, nonlin = 0.5): number {
  return (forceN / k) * (1 + nonlin * forceN ** 2) * 1000
}

/**
 * Шаг затухающих колебаний около равновесия yEq:
 * y'' = −ω²(y − yEq) − 2ζω·y'. Возвращает новые [y, v].
 */
export function oscStep(
  y: number,
  v: number,
  yEq: number,
  omega: number,
  zeta: number,
  dt: number,
): [number, number] {
  const a = -omega * omega * (y - yEq) - 2 * zeta * omega * v
  const v2 = v + a * dt
  return [y + v2 * dt, v2]
}

/**
 * Прогиб балки на двух опорах (±half от середины) под грузом в середине, в долях
 * прогиба середины yc. За опорами линейка идёт по касательной — вверх.
 */
export function beamShape(x: number, yc: number, half: number): number {
  const span = 2 * half
  const s = half - Math.abs(x)
  if (s >= 0) return (yc * s * (3 * span ** 2 - 4 * s ** 2)) / span ** 3
  return ((yc * 3) / span) * s
}

/** Длина пружины под силой F: l = l₀ + F/k (м, Н, Н/м). Удлинение — разность с l₀. */
export function springLength(l0: number, k: number, forceN: number): number {
  return l0 + forceN / k
}

export type Деформация = 'растяжение' | 'сжатие' | 'изгиб' | 'сдвиг' | 'кручение'

/**
 * Куда сдвигается точка бруска при деформации. Брусок — x ∈ [0, 1] вдоль, y ∈ [−0.5, 0.5]
 * поперёк (y вниз), левый торец закреплён; s ∈ [0, 1] — сила воздействия. Возвращает
 * новые [x, y]. Кручение на виде сбоку — поворот сечений, его рисует twistAngle.
 */
export function deformPoint(вид: Деформация, x: number, y: number, s: number): [number, number] {
  const e = 0.25 * s
  switch (вид) {
    case 'растяжение':
      return [x * (1 + e), y * (1 - 0.3 * e)] // длиннее и чуть тоньше
    case 'сжатие':
      return [x * (1 - e), y * (1 + 0.3 * e)] // короче и чуть толще
    case 'изгиб': {
      // консоль с силой на свободном конце: прогиб w ~ x²(3 − x)/2, сечения
      // поворачиваются вслед за наклоном оси — верхние волокна растянуты, нижние сжаты
      const w = (0.35 * s * x * x * (3 - x)) / 2
      const наклон = Math.atan((0.35 * s * (6 * x - 3 * x * x)) / 2)
      return [x - y * Math.sin(наклон), w + y * Math.cos(наклон)]
    }
    case 'сдвиг':
      // слои скользят вдоль параллельно друг другу: нижний закреплён, верхний уходит дальше всех
      return [x + 0.3 * s * (0.5 - y), y]
    case 'кручение':
      return [x, y]
  }
}

/** Угол закручивания сечения на расстоянии x от закреплённого торца, рад. */
export function twistAngle(x: number, s: number): number {
  return 1.2 * s * x
}

// ---------------------------------------------------------------- падение и воздух

/** Коэффициент сопротивления воздуха c в F = c·v²: c = ½ρC·S. */
export function dragCoef(areaM2: number, shapeC = 1.2, rho = 1.2): number {
  return 0.5 * rho * shapeC * areaM2
}

/**
 * Шаг падения вниз (y растёт вниз): a = g − (c/m)·v², без воздуха c = 0.
 * Возвращает новые [y, v].
 */
export function fallStep(y: number, v: number, massKg: number, c: number, dt: number, g = 9.8): [number, number] {
  const a = g - (c / massKg) * v * Math.abs(v)
  const v2 = v + a * dt
  return [y + v2 * dt, v2]
}

/** Время падения с высоты h (численно); без воздуха совпадает с √(2h/g). */
export function fallTime(h: number, massKg: number, c: number, g = 9.8): number {
  let y = 0
  let v = 0
  let t = 0
  const dt = 0.0005
  while (y < h && t < 60) {
    ;[y, v] = fallStep(y, v, massKg, c, dt, g)
    t += dt
  }
  return t
}

// ---------------------------------------------------------------- вес

/**
 * Вес тела на опоре или подвесе, движущемся с ускорением a (вверх — плюс):
 * P = m(g + a). Не бывает отрицательным: при a ≤ −g тело отрывается — невесомость.
 */
export function weightN(massKg: number, aUp: number, g = G): number {
  return Math.max(0, massKg * (g + aUp))
}

/** Показания пружинных весов, градуированных на Земле, в «килограммах» шкалы. */
export function springScaleKg(massKg: number, gPlanet: number, gEarth = 9.8): number {
  return (massKg * gPlanet) / gEarth
}

/**
 * Рычажные весы: момент силы тяжести слева минус справа (плечи равны). Знак — куда
 * наклон, ноль — равновесие. Равновесие не зависит от g: обе чашки умножаются на одно g.
 */
export function leverTorque(massLeft: number, massRight: number, g: number): number {
  return (massLeft - massRight) * g
}

export const ПЛАНЕТЫ = [
  { имя: 'Земля', g: 9.8 },
  { имя: 'Луна', g: 1.6 },
  { имя: 'Марс', g: 3.7 },
] as const

// ---------------------------------------------------------------- динамометр

/** Цена деления: разность соседних подписанных значений, делённая на число делений между ними. */
export function scaleDivision(a: number, b: number, n: number): number {
  return Math.abs(a - b) / n
}

/**
 * Показание динамометра: сила, округлённая до ближайшего деления, но не больше
 * предела измерения. overload — груз тянет сильнее предела.
 */
export function dynamometerReading(forceN: number, limit: number, division: number): {
  reading: number
  overload: boolean
} {
  const r = Math.round(Math.min(forceN, limit) / division) * division
  return { reading: Number(r.toFixed(6)), overload: forceN > limit + 1e-9 }
}

export const ДИНАМОМЕТРЫ = [
  { предел: 1, подписи: 0.2, делений: 10 }, // цена деления 0,02 Н
  { предел: 5, подписи: 1, делений: 10 }, // 0,1 Н
  { предел: 10, подписи: 2, делений: 10 }, // 0,2 Н
] as const

// ---------------------------------------------------------------- центр тяжести

export type Точка = [number, number]

/** Центр тяжести однородной пластины-многоугольника (формула площадей). */
export function polygonCentroid(p: readonly Точка[]): Точка {
  let a = 0
  let cx = 0
  let cy = 0
  for (let i = 0; i < p.length; i++) {
    const [x1, y1] = p[i]
    const [x2, y2] = p[(i + 1) % p.length]
    const cross = x1 * y2 - x2 * y1
    a += cross
    cx += (x1 + x2) * cross
    cy += (y1 + y2) * cross
  }
  a /= 2
  return [cx / (6 * a), cy / (6 * a)]
}

/**
 * Угол поворота пластины, подвешенной в точке pivot, в равновесии: центр тяжести
 * встаёт строго под точкой подвеса. Ось y экрана смотрит вниз; угол — по часовой, рад.
 */
export function hangAngle(pivot: Точка, com: Точка): number {
  // вектор «подвес → центр тяжести» поворачиваем до направления строго вниз (угол π/2)
  return Math.PI / 2 - Math.atan2(com[1] - pivot[1], com[0] - pivot[0])
}

/** Поворот точки p вокруг c на угол a (по часовой при оси y вниз). */
export function rotate(p: Точка, c: Точка, a: number): Точка {
  const dx = p[0] - c[0]
  const dy = p[1] - c[1]
  return [c[0] + dx * Math.cos(a) - dy * Math.sin(a), c[1] + dx * Math.sin(a) + dy * Math.cos(a)]
}

// ---------------------------------------------------------------- вращающаяся Земля

export const СУТКИ_ЗЕМЛИ_Ч = 24
const СЖАТИЕ_ЗЕМЛИ = 1 / 298.3 // при настоящих сутках
const R_ЭКВ_КМ = 6378
const G_ПОЛЮС = 9.832
const G_ЭКВ = 9.78

/**
 * Упрощённая модель вращающейся Земли: сплющенность и разность g растут как квадрат
 * угловой скорости (центробежный эффект ~ ω²). При 24-часовых сутках — настоящие
 * числа: полярный радиус меньше экваториального на 21 км, g на полюсе 9,83,
 * на экваторе 9,78 м/с².
 */
export function rotatingEarth(суткиЧ: number): { сжатие: number; разностьКм: number; gПолюс: number; gЭкватор: number } {
  const q = (СУТКИ_ЗЕМЛИ_Ч / суткиЧ) ** 2
  const сжатие = СЖАТИЕ_ЗЕМЛИ * q
  return {
    сжатие,
    разностьКм: R_ЭКВ_КМ * сжатие,
    gПолюс: G_ПОЛЮС,
    gЭкватор: G_ПОЛЮС - (G_ПОЛЮС - G_ЭКВ) * q,
  }
}

// ---------------------------------------------------------------- величины и единицы (уроки 1–3)

/** Измерение — сравнение с единицей: сколько раз единица укладывается в длине. */
export function countUnits(length: number, unit: number): number {
  return length / unit
}

/** Подручные единицы длины из урока 3 (м): пядь, карандаш, «попугай» и «мартышка» из мультфильма. */
export const ЕДИНИЦЫ_ДЛИНЫ = [
  { имя: 'пядь', длина: 0.2 },
  { имя: 'карандаш', длина: 0.175 },
  { имя: 'попугай', длина: 0.1 }, // удав 3,8 м — 38 попугаев
  { имя: 'мартышка', длина: 0.76 }, // и 5 мартышек
  { имя: 'метр', длина: 1 },
] as const

/** Десятичные приставки через три порядка — те, что нужны в 7 классе. */
export const ПРИСТАВКИ = [
  { приставка: 'нано', обозначение: 'н', степень: -9 },
  { приставка: 'микро', обозначение: 'мк', степень: -6 },
  { приставка: 'милли', обозначение: 'м', степень: -3 },
  { приставка: '', обозначение: '', степень: 0 },
  { приставка: 'кило', обозначение: 'к', степень: 3 },
  { приставка: 'мега', обозначение: 'М', степень: 6 },
  { приставка: 'гига', обозначение: 'Г', степень: 9 },
] as const

/**
 * Длина в метрах — удобным числом с приставкой: число от 1 до 1000, кроме краёв таблицы
 * (меньше нанометра — доли нм, больше гигаметра — тысячи Гм).
 */
export function withPrefix(meters: number): { число: number; приставка: (typeof ПРИСТАВКИ)[number] } {
  let выбор: (typeof ПРИСТАВКИ)[number] = ПРИСТАВКИ[0]
  for (const п of ПРИСТАВКИ) if (meters >= 10 ** п.степень * 0.9999999) выбор = п
  return { число: meters / 10 ** выбор.степень, приставка: выбор }
}

/** Объекты «Лестницы масштабов», размер — в метрах. */
export const МАСШТАБЫ = [
  { что: 'атом', размер: 1e-10 },
  { что: 'молекула белка', размер: 1e-8 },
  { что: 'вирус', размер: 1e-7 },
  { что: 'бактерия', размер: 2e-6 },
  { что: 'толщина волоса', размер: 7e-5 },
  { что: 'муравей', размер: 5e-3 },
  { что: 'ладонь', размер: 0.1 },
  { что: 'человек', размер: 1.7 },
  { что: 'девятиэтажный дом', размер: 30 },
  { что: 'Эверест', размер: 8.8e3 },
  { что: 'Земля (диаметр)', размер: 1.27e7 },
  { что: 'от Земли до Луны', размер: 3.84e8 },
  { что: 'Солнце (диаметр)', размер: 1.39e9 },
  { что: 'от Земли до Солнца', размер: 1.5e11 },
] as const

// ---------------------------------------------------------------- опыты-загадки уроков 1–3

/**
 * Лодочка на слюде (урок 1): горячая лодочка сама раскачивается. Нагретая сверху слюда вздувается
 * бугорком под лодочкой и подталкивает её — колебания подпитываются, пока есть жар (модель
 * Ван дер Поля: размах выходит на постоянный, около 2·A). Остыла — обычные затухающие колебания.
 * heat — от 0 (холодная) до 1 (горит). Возвращает новые [x, v].
 */
export function boatStep(x: number, v: number, heat: number, dt: number, omega = 6, A = 1): [number, number] {
  const подпитка = 1.5 * heat * (1 - (x / A) ** 2) * v // бугорок толкает в сторону движения
  const трение = 0.5 * (1 - heat) * v
  const a = -omega * omega * x + подпитка - трение
  const v2 = v + a * dt
  return [x + v2 * dt, v2]
}

/** Закон Малюса: доля света, прошедшего через два поляроида под углом θ (градусы). */
export function malus(angleDeg: number): number {
  return Math.cos((angleDeg * Math.PI) / 180) ** 2
}

/**
 * Что «скажет» рука в воде (уроки 2–3): ощущение зависит не от температуры воды, а от того,
 * теплее она или холоднее, чем то, к чему рука привыкла. Порог — 2 °C.
 */
export function feltTemperature(adaptedTo: number, water: number): 'холодно' | 'тепло' | 'не чувствую разницы' {
  if (water < adaptedTo - 2) return 'холодно'
  if (water > adaptedTo + 2) return 'тепло'
  return 'не чувствую разницы'
}

/**
 * Балерина на льду (урок 3): момент импульса сохраняется, I·ω = const. Тело — 2 кг·м²,
 * руки — две массы по 3 кг на расстоянии r от оси. Возвращает угловую скорость при новом r.
 */
export function spinRate(omega0: number, r0: number, r: number, body = 2, arm = 3): number {
  const I = (rr: number) => body + 2 * arm * rr * rr
  return (omega0 * I(r0)) / I(r)
}

// ---------------------------------------------------------------- шкала и нониус (урок 4)

/** Шкалы приборов урока 4: подписи через шаг «подписи», между соседними подписями «делений» делений. */
export const ШКАЛЫ = [
  { прибор: 'термометр комнатный', единица: '°C', от: -10, до: 50, подписи: 10, делений: 2 },
  { прибор: 'линейка', единица: 'см', от: 0, до: 5, подписи: 1, делений: 10 },
  { прибор: 'мерный цилиндр', единица: 'мл', от: 0, до: 100, подписи: 10, делений: 10 },
  { прибор: 'шприц на 5 мл', единица: 'мл', от: 0, до: 5, подписи: 1, делений: 5 },
  { прибор: 'термометр медицинский', единица: '°C', от: 34, до: 42, подписи: 1, делений: 10 },
  { прибор: 'термометр лабораторный', единица: '°C', от: 0, до: 100, подписи: 20, делений: 10 },
] as const

/** Показание по шкале: значение, округлённое до ближайшего деления (глаз отсчитывает по штрихам). */
export function scaleReading(value: number, division: number): number {
  return Number((Math.round(value / division) * division).toFixed(6))
}

/**
 * Нониус штангенциркуля: основная шкала — миллиметры, десять делений нониуса равны девяти мм
 * (каждое 0,9 мм). Целые мм читают по нулю нониуса, десятые — по номеру штриха нониуса,
 * совпавшего со штрихом основной шкалы.
 */
export function vernier(xMm: number): { целых: number; штрих: number; отсчёт: number } {
  const десятых = Math.round(xMm * 10)
  const целых = Math.floor(десятых / 10)
  const штрих = десятых - целых * 10
  return { целых, штрих, отсчёт: целых + штрих / 10 }
}

// ---------------------------------------------------------------- площадь (урок 5)

/** Площадь многоугольника (формула площадей Гаусса), всегда положительная. */
export function polygonArea(p: readonly Точка[]): number {
  let a = 0
  for (let i = 0; i < p.length; i++) {
    const [x1, y1] = p[i]
    const [x2, y2] = p[(i + 1) % p.length]
    a += x1 * y2 - x2 * y1
  }
  return Math.abs(a) / 2
}

/** Точка внутри многоугольника: луч вправо пересекает границу нечётное число раз. */
export function insidePolygon(t: Точка, p: readonly Точка[]): boolean {
  let внутри = false
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    const [xi, yi] = p[i]
    const [xj, yj] = p[j]
    if (yi > t[1] !== yj > t[1] && t[0] < ((xj - xi) * (t[1] - yi)) / (yj - yi) + xi) внутри = !внутри
  }
  return внутри
}

/** Клетка палетки: левый верхний угол и вид — полная или неполная. */
export type Клетка = { x: number; y: number; полная: boolean }

/**
 * Метод палетки: клетки со стороной cell. Полная — вся внутри фигуры, неполная — задета
 * фигурой частично (проверка сеткой 6×6 точек внутри клетки). Площадь — S₁·(N + K/2).
 */
export function palette(p: readonly Точка[], cell: number): { N: number; K: number; S: number; клетки: Клетка[] } {
  const xs = p.map((t) => t[0])
  const ys = p.map((t) => t[1])
  const клетки: Клетка[] = []
  const ТОЧЕК = 6
  for (let x = Math.floor(Math.min(...xs) / cell) * cell; x < Math.max(...xs); x += cell) {
    for (let y = Math.floor(Math.min(...ys) / cell) * cell; y < Math.max(...ys); y += cell) {
      let внутри = 0
      for (let i = 0; i < ТОЧЕК; i++)
        for (let j = 0; j < ТОЧЕК; j++)
          if (insidePolygon([x + ((i + 0.5) * cell) / ТОЧЕК, y + ((j + 0.5) * cell) / ТОЧЕК], p)) внутри++
      if (внутри > 0) клетки.push({ x, y, полная: внутри === ТОЧЕК * ТОЧЕК })
    }
  }
  const N = клетки.filter((к) => к.полная).length
  const K = клетки.length - N
  return { N, K, S: cell * cell * (N + K / 2), клетки }
}

/** Длина окружности: πd — путь колеса за один оборот. */
export function circumference(d: number): number {
  return Math.PI * d
}

// ---------------------------------------------------------------- объём (урок 6)

/**
 * Мензурка с телом: уровень поднимается на объём погружённой части. depth — доля тела под
 * водой (0…1); выше вместимости уровень не поднимется — вода перельётся.
 */
export function beakerLevel(v1: number, vBody: number, depth: number, capacity: number): number {
  return Math.min(v1 + vBody * Math.min(Math.max(depth, 0), 1), capacity)
}

/** Отливной сосуд налит до носика: всё, что вытеснила погружённая часть тела, вытекает в мензурку. */
export function overflowOut(vBody: number, depth: number): number {
  return vBody * Math.min(Math.max(depth, 0), 1)
}

// ---------------------------------------------------------------- сложение сил (урок 38)

/** Равнодействующая сил по одной прямой: силы со знаком (плюс — вниз или вправо). */
export function resultant(forces: readonly number[]): number {
  return forces.reduce((a, b) => a + b, 0)
}

// ---------------------------------------------------------------- трение (урок 39)

/**
 * Брусок тянут динамометром по столу. Пока тяга не больше наибольшей силы трения покоя μ·N,
 * брусок стоит и трение равно тяге; поехал — трение скольжения μ·N, лишняя сила его разгоняет.
 * Сила нормального давления N равна весу бруска; a — ускорение, м/с².
 */
export function friction(pull: number, mu: number, normal: number): { Fтр: number; едет: boolean; a: number } {
  const предел = mu * normal
  if (pull <= предел + 1e-9) return { Fтр: pull, едет: false, a: 0 }
  return { Fтр: предел, едет: true, a: ((pull - предел) / normal) * G }
}

// ---------------------------------------------------------------- перевод единиц (урок 8)

/**
 * Перевод в основную единицу: приставку заменяют её множителем 10^степень. У площади
 * множитель возводят в квадрат, у объёма — в куб: 1 мм² = (10⁻³ м)² = 10⁻⁶ м².
 */
export function toBaseUnit(value: number, степень: number, мерность: 1 | 2 | 3): number {
  return value * 10 ** (степень * мерность)
}

/** Скорость: 1 м/с = 3,6 км/ч (в часе 3600 с, в километре 1000 м). */
export function kmhToMs(kmh: number): number {
  return kmh / 3.6
}

// ---------------------------------------------------------------- мега-, макро- и микромир (урок 9)

export const СКОРОСТЬ_СВЕТА = 3e8 // м/с, как в уроке
export const ГОД_С = 365 * 86400 // упрощённый год, с
export const СВЕТОВОЙ_ГОД_М = СКОРОСТЬ_СВЕТА * ГОД_С

/** Сколько лет назад вышел свет, который мы видим сейчас от объекта на расстоянии d, м. */
export function lightAgeYears(dM: number): number {
  return dM / СКОРОСТЬ_СВЕТА / ГОД_С
}

/** Верхняя оценка размера Вселенной: свет за всё её время, ≈ c·t. */
export function universeSizeM(ageYears: number): number {
  return СКОРОСТЬ_СВЕТА * ageYears * ГОД_С
}

/** Какой из трёх миров: микромир — меньше атома (10⁻⁹ м), мегамир — больше 10¹⁵ м. */
export function мирПоРазмеру(м: number): 'микромир' | 'макромир' | 'мегамир' {
  if (м < 1e-9) return 'микромир'
  if (м > 1e15) return 'мегамир'
  return 'макромир'
}

// ---------------------------------------------------------------- самодельная мензурка (урок 10)

/** Объём усечённого конуса (стаканчика) с радиусами дна r0 и на высоте h — r(h). */
function cupVolume(h: number, r0: number, r1: number, H: number): number {
  const r = r0 + ((r1 - r0) * h) / H
  return (Math.PI * h * (r0 * r0 + r0 * r + r * r)) / 3
}

/**
 * Высота воды в стаканчике (дно r0, верх r1, высота H) при объёме V — делением отрезка пополам.
 * У расширяющегося кверху стакана одинаковые порции дают всё более тесные метки.
 */
export function cupLevel(V: number, r0: number, r1: number, H: number): number {
  let lo = 0
  let hi = H
  if (cupVolume(H, r0, r1, H) <= V) return H
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2
    if (cupVolume(mid, r0, r1, H) < V) lo = mid
    else hi = mid
  }
  return (lo + hi) / 2
}

/**
 * Параллакс: шкала снаружи стенки, край воды — на глубине g за ней, глаз — на расстоянии D
 * от шкалы на высоте eye. Луч «глаз → край воды» пересекает шкалу не там, где вода:
 * отсчёт = eye + (level − eye)·D/(D + g). Глаз на уровне воды — ошибки нет.
 */
export function parallaxReading(level: number, eye: number, D: number, g: number): number {
  return eye + ((level - eye) * D) / (D + g)
}

// ---------------------------------------------------------------- точность измерений (урок 7)

/** Приборы урока 7 и их точность отсчёта, мм: лента и линейка — половина деления, у остальных — написана на приборе. */
export const ПРИБОРЫ_ДЛИНЫ = [
  { прибор: 'мерная лента', деление: 5, точность: 2.5 },
  { прибор: 'линейка', деление: 1, точность: 0.5 },
  { прибор: 'штангенциркуль', деление: 0.1, точность: 0.1 },
  { прибор: 'микрометр', деление: 0.01, точность: 0.01 },
] as const

/** Отсчёт прибором: истинное значение, округлённое до деления прибора. */
export function measureWith(trueMm: number, деление: number): number {
  return Number((Math.round(trueMm / деление) * деление).toFixed(6))
}

/**
 * Число значащих цифр в записи числа: нули слева не значащие (задают масштаб),
 * нули справа после запятой — значащие. «0,02059» — 4, «2,00» — 3.
 */
export function significantDigits(запись: string): number {
  const цифры = запись.trim().replace(',', '.').replace(/^[-+]/, '').replace(/[eE].*$/, '')
  const безТочки = цифры.replace('.', '').replace(/^0+/, '')
  return цифры.includes('.') ? безТочки.length : безТочки.replace(/0+$/, '').length || 1
}

/** Округление до n значащих цифр: 11,616 при двух цифрах — 12. */
export function roundSig(x: number, n: number): number {
  if (x === 0) return 0
  const порядок = Math.floor(Math.log10(Math.abs(x)))
  const шаг = 10 ** (порядок - n + 1)
  return Number((Math.round(x / шаг) * шаг).toPrecision(n))
}

/* ---------- Строение вещества: атомы и молекулы (урок 11) ---------- */

export const АТМОСФЕРА = 1e5 // Па
/** Сжимаемость воды: под лишней атмосферой объём уменьшается на 0,005 %. */
export const СЖИМАЕМОСТЬ_ВОДЫ = 4.6e-10 // 1/Па

/**
 * Воздух в закрытом шприце (урок 11): температура не меняется, pV = const.
 * Сила F (Н) давит на поршень площадью S (м²) поверх атмосферы.
 */
export function gasVolume(V0: number, F: number, S: number, p0 = АТМОСФЕРА): number {
  return (V0 * p0) / (p0 + F / S)
}

/** Вода в том же шприце: промежутки между молекулами почти не меняются. */
export function waterVolume(V0: number, F: number, S: number): number {
  return V0 * (1 - СЖИМАЕМОСТЬ_ВОДЫ * (F / S))
}

export const ЛАТУНЬ_АЛЬФА = 19e-6 // 1/°C — линейное расширение латуни

/** Размер латунной детали при температуре t; отверстие в кольце растёт так же, как само кольцо. */
export function heatedSize(d20: number, t: number, alpha = ЛАТУНЬ_АЛЬФА): number {
  return d20 * (1 + alpha * (t - 20))
}

/** Прибор «шар и кольцо»: шар проходит, пока он не больше отверстия. */
export function ballPasses(dBall20: number, dHole20: number, tBall: number, tRing: number): boolean {
  return heatedSize(dBall20, tBall) <= heatedSize(dHole20, tRing)
}

/** Доля объёма, которую занимают одинаковые шары, насыпанные как попало. */
export const УКЛАДКА = 0.62

/**
 * Смесь сыпучих (урок 11): мелкое (горох, песок) засыпает пустоты между крупным (картошка,
 * камни) — пока пустот хватает, объём смеси равен объёму крупного. Одинаковые по размеру
 * (картошка и яблоки) пустот друг другу не заполняют — объёмы складываются.
 */
export function bulkMix(vBig: number, vSmall: number, мелкоеВлезает: boolean): number {
  if (!мелкоеВлезает) return vBig + vSmall
  const пустоты = vBig * (1 - УКЛАДКА)
  return vBig + Math.max(0, vSmall - пустоты)
}

/** 100 мл воды и 100 мл спирта дают около 193 мл смеси (табличное сжатие для смеси 1:1). */
export const ВОДА_И_СПИРТ_100_100 = 193

/* ---------- Движение молекул: диффузия, броуновское движение (урок 12) ---------- */

export const R_ГАЗ = 8.31 // Дж/(моль·К)
export const K_БОЛЬЦМАН = 1.38e-23 // Дж/К

/** Средняя скорость теплового движения молекул, м/с: азот при 20 °C — около 500 м/с. */
export function molecularSpeed(tC: number, M = 0.028): number {
  return Math.sqrt((3 * R_ГАЗ * (tC + 273)) / M)
}

/**
 * Во сколько раз быстрее идёт диффузия в твёрдом теле при температуре t, чем при 20 °C.
 * Атому надо «перепрыгнуть» на соседнее место; число прыжков растёт с температурой очень резко:
 * золото и свинец, нагретые до 330 °C, перемешиваются за часы, а не за годы (урок 12).
 */
export function solidHopRatio(tC: number, барьерK = 5270): number {
  return Math.exp(барьерK * (1 / 293 - 1 / (tC + 273)))
}

/**
 * Насколько перемешаны два вещества в сосуде шириной W: в начале первое слева, второе справа.
 * 0 — не перемешаны, 1 — частиц каждого вида в обеих половинах поровну.
 */
export function mixing(xs: readonly number[], kinds: readonly (0 | 1)[], W: number): number {
  let чужие = 0
  xs.forEach((x, i) => {
    if ((kinds[i] === 0) === x > W / 2) чужие++
  })
  return Math.min(1, (2 * чужие) / Math.max(xs.length, 1))
}

/**
 * Броуновская частица радиусом r (м) в воде: D = kT / (6πηr) (Эйнштейн, 1905).
 * Мелкая частица «шустрее», в горячей воде — тоже.
 */
export function brownD(r: number, tC: number, eta = 1e-3): number {
  return (K_БОЛЬЦМАН * (tC + 273)) / (6 * Math.PI * eta * r)
}

/** Число со стандартным нормальным распределением (Бокс — Мюллер). */
export function gauss(rand: () => number = Math.random): number {
  const u = Math.max(rand(), 1e-12)
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * rand())
}

export const ПРОБЕГ_В_ВОЗДУХЕ = 6.8e-8 // м — путь молекулы воздуха между ударами

/** Сколько летит молекула без ударов по прямой на расстояние L, с. */
export function straightTime(L: number, v: number): number {
  return L / v
}

/**
 * Сколько молекула запаха уходит на расстояние L в неподвижном воздухе: после N ударов она
 * в среднем отходит на λ·√N, поэтому нужно (L/λ)² ударов, по λ/v секунд на каждый.
 */
export function walkTime(L: number, v: number, lambda = ПРОБЕГ_В_ВОЗДУХЕ): number {
  return (L * L) / (lambda * v)
}
